import React, { useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  DatePicker,
  message,
  Alert,
  Space,
  Typography,
  Breadcrumb,
  Progress,
} from 'antd';
import {
  LinkOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { certificationAPI } from '../services/api';
import AppLayout from '../components/AppLayout';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

function CertificationUpload() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleSubmit = async (values) => {
    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('url', values.url);
      formData.append('title', values.title);
      formData.append('type', 'ai-certification');

      if (values.completion_date) {
        formData.append(
          'completion_date',
          values.completion_date.format('YYYY-MM-DD')
        );
      }

      if (values.notes) {
        formData.append('notes', values.notes);
      }

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await certificationAPI.upload(formData);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.status === 'success') {
        message.success('Sertifikasi berhasil disimpan!');
        form.resetFields();
        setTimeout(() => {
          navigate('/certifications');
        }, 1500);
      } else {
        message.error(response.message || 'Gagal menyimpan sertifikasi');
        setUploadProgress(0);
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.message ||
                       error.message ||
                       'Gagal menyimpan sertifikasi. Silakan coba lagi.';
      message.error(errorMsg);
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  return (
    <AppLayout>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Breadcrumb */}
        <Breadcrumb style={{ marginBottom: 16 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Sertifikasi AI</Breadcrumb.Item>
          <Breadcrumb.Item>Upload Sertifikasi</Breadcrumb.Item>
        </Breadcrumb>

        {/* Header */}
        <Card style={{ marginBottom: '24px' }}>
          <Title level={2}>
            <LinkOutlined /> Tambah Sertifikasi AI
          </Title>
          <Paragraph>
            Upload sertifikasi AI Anda dengan mudah - cukup URL dan judul saja!
            Sistem akan otomatis menghubungkan ke profil Anda berdasarkan email login.
          </Paragraph>
          <Paragraph type="secondary" style={{ fontSize: '14px', marginBottom: 0 }}>
            💡 <strong>Cara kerja:</strong> Anda input URL + judul → Status pending →
            Admin verifikasi → Otomatis +1 poin ke leaderboard Anda!
          </Paragraph>
        </Card>

        {/* Info Alert */}
        <Alert
          message="📋 Panduan Upload (SIMPLE - Hanya URL + Judul!)"
          description={
            <div>
              <Text strong>Yang Diperlukan:</Text>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>✅ <strong>URL sertifikat</strong> - Link dari Google Drive, Coursera, atau platform lainnya</li>
                <li>✅ <strong>Judul sertifikat</strong> - Nama lengkap sertifikat Anda</li>
              </ul>
              <Text strong>TIDAK Perlu:</Text>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>❌ Upload file PDF (hanya URL)</li>
                <li>❌ Pilih jenis sertifikat (semua treated sama)</li>
                <li>❌ Input NIM/NIP (sistem detect otomatis by email)</li>
              </ul>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                <strong>Scoring:</strong> Setiap sertifikasi yang diverifikasi = <strong style={{ color: '#14B8A6' }}>+1 poin</strong> di leaderboard! 🏆
              </Text>
            </div>
          }
          type="info"
          icon={<InfoCircleOutlined />}
          showIcon
          style={{ marginBottom: '24px' }}
        />

        {/* Upload Form */}
        <Card>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            {/* URL Input */}
            <Form.Item
              name="url"
              label={
                <span>
                  <LinkOutlined /> URL Sertifikat <Text type="danger">*</Text>
                </span>
              }
              rules={[
                { required: true, message: 'Silakan masukkan URL sertifikasi' },
                { type: 'url', message: 'Format URL tidak valid (harus dimulai https://)' },
                { max: 1000, message: 'URL maksimal 1000 karakter' },
              ]}
              extra={
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  💡 Masukkan link sertifikat dari Google Drive, Coursera, atau platform lainnya.
                  TIDAK perlu upload PDF - cukup URL saja!
                </Text>
              }
            >
              <Input
                prefix={<LinkOutlined style={{ color: '#14B8A6' }} />}
                placeholder="https://drive.google.com/file/d/xxxxx atau https://coursera.org/share/xxxxx"
                disabled={uploading}
                size="large"
              />
            </Form.Item>

            {/* Title */}
            <Form.Item
              name="title"
              label={
                <span>
                  Judul Sertifikasi <Text type="danger">*</Text>
                </span>
              }
              rules={[
                { required: true, message: 'Silakan masukkan judul sertifikasi' },
                { max: 500, message: 'Judul maksimal 500 karakter' },
              ]}
              extra={
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  💡 Tulis nama lengkap sertifikat (contoh: Google AI Essentials for Educators)
                </Text>
              }
            >
              <Input
                placeholder="Nama lengkap sertifikat Anda"
                disabled={uploading}
                size="large"
              />
            </Form.Item>

            {/* Completion Date */}
            <Form.Item
              name="completion_date"
              label="Tanggal Selesai (Opsional)"
              extra={
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Kapan Anda menyelesaikan sertifikasi ini
                </Text>
              }
            >
              <DatePicker
                style={{ width: '100%' }}
                format="DD MMMM YYYY"
                placeholder="Pilih tanggal"
                disabled={uploading}
                size="large"
              />
            </Form.Item>

            {/* Notes */}
            <Form.Item
              name="notes"
              label="Catatan Tambahan (Opsional)"
              extra={
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Informasi tambahan jika ada (tidak wajib diisi)
                </Text>
              }
            >
              <TextArea
                rows={3}
                placeholder="Contoh: Sertifikasi diselesaikan dengan nilai sempurna"
                disabled={uploading}
                maxLength={1000}
                showCount
              />
            </Form.Item>

            {/* Upload Progress */}
            {uploading && uploadProgress > 0 && (
              <Form.Item>
                <Progress percent={uploadProgress} status="active" />
                <Text type="secondary">Menyimpan sertifikasi...</Text>
              </Form.Item>
            )}

            {/* Submit Buttons */}
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<CheckCircleOutlined />}
                  loading={uploading}
                >
                  {uploading ? 'Menyimpan...' : 'Simpan Sertifikasi'}
                </Button>
                <Button onClick={() => navigate('/certifications')} disabled={uploading}>
                  Batal
                </Button>
                <Button
                  type="link"
                  onClick={() => navigate('/certifications/info')}
                  disabled={uploading}
                >
                  <InfoCircleOutlined /> Panduan Upload
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        {/* Additional Info */}
        <Card style={{ marginTop: '24px' }}>
          <Title level={5}>🎯 Setelah Upload</Title>
          <Paragraph>
            <strong>Proses Otomatis:</strong> Setelah Anda menyimpan link sertifikasi,
            sistem akan otomatis menghubungkan sertifikat ke profil Anda berdasarkan
            email login. Admin hanya perlu memverifikasi keaslian sertifikat - tidak
            perlu input NIM/NIP manual!
          </Paragraph>
          <Paragraph>
            <strong>Timeline:</strong> Proses verifikasi admin biasanya memakan waktu
            1-3 hari kerja. Anda dapat melihat status verifikasi di halaman
            &quot;Sertifikasi Saya&quot;.
          </Paragraph>
          <Paragraph style={{ marginBottom: 0 }}>
            <strong>Hasil:</strong> Jika sertifikasi Anda diverifikasi, sertifikat
            akan ditampilkan di profil Anda dan otomatis menambahkan{' '}
            <Text strong style={{ color: '#14B8A6' }}>+1 poin</Text> ke leaderboard!
          </Paragraph>
        </Card>
      </div>
    </AppLayout>
  );
}

export default CertificationUpload;
